import React, { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Table, notification, Skeleton } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import SidebarPage from '../../../layout/SidebarPage/SidebarPage';
import PageTitle from '../../../layout/PageTitle/PageTitle';
import { useGet, usePost } from '../../../hooks/useApi';
import {
  ReportAccount,
  DelinquentAccountsResponse,
  ExportResponse,
} from '../../../models/admin/reports.model';

const floatToDollar = (value: number): string => {
  return value.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
};

const DelinquentAccounts: FC = () => {
  const navigate = useNavigate();
  const [isExporting, setIsExporting] = useState<boolean>(false);

  // Fetch delinquent accounts
  const { data, isLoading, error } = useGet<DelinquentAccountsResponse>(
    '/v1/auth/admin/reports/delinquent'
  );

  // Export mutation
  const exportMutation = usePost<ExportResponse, void>(
    '/v1/auth/admin/reports/delinquent/export'
  );

  const handleExport = () => {
    setIsExporting(true);
    exportMutation.mutate(undefined, {
      onSuccess: (response) => {
        if (response.downloadUrl) {
          window.open(response.downloadUrl, '_blank');
          notification.success({
            message: 'Export Started',
            description: 'Your CSV file is ready for download.',
          });
        }
        setIsExporting(false);
      },
      onError: (error: any) => {
        notification.error({
          message: 'Export Failed',
          description:
            error.response?.data?.message ||
            'Failed to export delinquent accounts.',
        });
        setIsExporting(false);
      },
    });
  };

  const handleRowClick = (record: ReportAccount) => {
    navigate(`/admin/ledger/${record.primaryId}`);
  };

  const columns: ColumnsType<ReportAccount> = [
    {
      title: 'Primary Name',
      dataIndex: 'primaryName',
      key: 'primaryName',
      sorter: (a, b) => a.primaryName.localeCompare(b.primaryName),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: (a, b) => a.email.localeCompare(b.email),
    },
    {
      title: 'Balance',
      dataIndex: 'balance',
      key: 'balance',
      render: (balance: number) => (
        <span style={{ color: balance > 0 ? 'red' : 'inherit' }}>
          ${floatToDollar(balance)}
        </span>
      ),
      sorter: (a, b) => a.balance - b.balance,
      defaultSortOrder: 'descend',
    },
    {
      title: 'School',
      dataIndex: 'schoolName',
      key: 'schoolName',
      sorter: (a, b) => a.schoolName.localeCompare(b.schoolName),
    },
  ];

  if (error) {
    return (
      <SidebarPage>
        <PageTitle>Delinquent Accounts</PageTitle>
        <Card>
          <p>Error loading delinquent accounts. Please try again.</p>
        </Card>
      </SidebarPage>
    );
  }

  return (
    <SidebarPage>
      <PageTitle>Delinquent Accounts</PageTitle>

      <Card>
        {isLoading ? (
          <Skeleton active />
        ) : (
          <>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 16,
              }}
            >
              <strong>
                Total Delinquent Accounts: {data?.totalCount || 0}
              </strong>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleExport}
                loading={isExporting}
                disabled={isLoading || !data || data.accounts.length === 0}
              >
                Download CSV
              </Button>
            </div>
            <Table
              columns={columns}
              dataSource={data?.accounts || []}
              rowKey="primaryId"
              pagination={{
                pageSize: 50,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} accounts`,
              }}
              onRow={(record) => ({
                onClick: () => handleRowClick(record),
                style: { cursor: 'pointer' },
              })}
            />
          </>
        )}
      </Card>
    </SidebarPage>
  );
};

export default DelinquentAccounts;
